// Third party
import React from 'react';

// Utils
import { canAccessUploadCSV } from '@/utils/feature-access';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

// Components
import { CloseButton } from '@/views/components/buttons';
import EmptyComparisonButton from '../misc/EmptyComparisonButton';

// Assets
import CreatePortfolioIcon from '@/images/icons/create-portfolio.svg';
import LoadClientIcon from '@/images/icons/load-client.svg';
import LoadModelIcon from '@/images/icons/load-model.svg';
import UploadPortfolioIcon from '@/images/icons/upload-portfolio.svg';

// Types
import User from '@/types/user';

// Legacy support
import LegacyComparisonQuickSearch from '@/views/containers/comparison/misc/LegacyComparisonQuickSearch';

type Props = {
  user: User;
  onCloseComparison: () => void;
  showModelPortfoliosModal: () => void;
  showContactPortfoliosModal: () => void;
  showEditPortfolioModal: () => void;
  showPortfolioUploadModal: () => void;
};

const DefaultNewComparison = (props: Props) => {
  const options = [
    {
      onClick: props.showModelPortfoliosModal,
      title: 'EmptyComparison.LoadModelPortfolio.Title',
      subtitle: 'EmptyComparison.LoadModelPortfolio.Subtitle',
      icon: LoadModelIcon,
      cy: 'emptyComparison-emptyComparisonButton-loadModelPortfolio',
      hidden: !userHasPermission(props.user, PERMISSIONS.USER_MODELS) && !userHasPermission(props.user, PERMISSIONS.ENTERPRISE_MODELS_LIST),
    },
    {
      onClick: props.showContactPortfoliosModal,
      title: 'EmptyComparison.LoadClientPortfolio.Title',
      subtitle: 'EmptyComparison.LoadClientPortfolio.Subtitle',
      icon: LoadClientIcon,
      cy: 'emptyComparison-emptyComparisonButton-loadClientPortfolio',
    },
    {
      onClick: props.showEditPortfolioModal,
      title: 'EmptyComparison.CreateNewPortfolio.Title',
      subtitle: 'EmptyComparison.CreateNewPortfolio.Subtitle',
      icon: CreatePortfolioIcon,
      cy: 'emptyComparison-emptyComparisonButton-createPortfolio',
    },
    {
      onClick: props.showPortfolioUploadModal,
      title: 'EmptyComparison.UploadPortfolio.Title',
      subtitle: 'EmptyComparison.UploadPortfolio.Subtitle',
      icon: UploadPortfolioIcon,
      cy: 'emptyComparison-emptyComparisonButton-uploadPortfolio',
      hidden: !canAccessUploadCSV(props.user),
    },
  ].filter(option => !option.hidden);

  const filteredOptions = options.filter(i => !i.hidden);

  return (
    <div className="empty-comparison">
      <CloseButton dark withText onClick={props.onCloseComparison} />

      <div className="empty-comparison-container">
        <div className="empty-comparison-content">
          <header>
            <FormattedMessage tagName="h1" id="EmptyComparison.Title" />
            <FormattedMessage tagName="p" id="EmptyComparison.Subtitle" />
          </header>

          {/* TODO: The v2 component 'ComparisonQuickSearch' has been created and requires implementation and testing. */}
          <LegacyComparisonQuickSearch user={props.user} />

          <div className="empty-options" style={{ '--option-count': filteredOptions.length > 3 ? 3 : filteredOptions.length } as React.CSSProperties}>
            {filteredOptions.map((option, index) => (
              <EmptyComparisonButton
                key={index}
                onClick={option.onClick}
                title={formatMessage(option.title)}
                subtitle={formatMessage(option.subtitle)}
                icon={option.icon}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefaultNewComparison;
